import { ABILITY_TUNING, NO_EFFECTS, abilityCost, antibioticTargets, chargeEnergy, dropInfection, energyGain, } from './abilities.js';
import { EMPTY, applyMask, countViruses, createBoard, indexOf } from './board.js';
import { bossTurn, createBossState, hitBoss, placeBoss, placeGuards, } from './boss.js';
import { applyGravity } from './gravity-resolver.js';
import { clearCells, findMatches } from './match-resolver.js';
import { canPlace, dropPosition, tryRotate, tryShift } from './movement-resolver.js';
import { pieceCells } from './piece.js';
import { generatePieceColors } from './piece-generator.js';
import { deriveSeed, normalizeSeed } from './rng.js';
import { createRules } from './rules.js';
import { scoreClear } from './scoring-system.js';
import { placeViruses } from './virus-placement.js';
/** Соли для независимых потоков случайных чисел из одного seed. */
const VIRUS_STREAM = 0x9e3779b9;
const PIECE_STREAM = 0x7f4a7c15;
const BOSS_STREAM = 0x2545f491;
const ATTACK_STREAM = 0x68e31da4;
function spawnPiece(state, events) {
    // Из генератора всегда берётся одна капсула: она либо сразу становится активной (previewSize = 0),
    // либо встаёт в конец очереди, а активной становится первая из очереди.
    const generated = generatePieceColors(state.pieceRng, state.rules.colorCount);
    const queued = state.next[0];
    const colors = queued ?? generated.colors;
    const next = queued ? [...state.next.slice(1), generated.colors] : state.next;
    const pieceRng = generated.state;
    const piece = { x: state.rules.spawn.x, y: state.rules.spawn.y, rotation: 0, colors };
    if (!canPlace(state.board, piece)) {
        events.push({ type: 'game-lost', reason: 'overflow' });
        return { ...state, next, pieceRng, active: null, status: 'lost', lossReason: 'overflow' };
    }
    events.push({ type: 'piece-spawned', piece });
    return offerNextOptions({ ...state, next, pieceRng, active: piece }, events);
}
/**
 * Варианты следующей капсулы для Стратега. Вариант 0 — текущая next[0]; остальные берутся
 * из того же генератора капсул (детерминированно), по возможности без повторов.
 */
function offerNextOptions(state, events) {
    const count = state.rules.nextChoiceCount;
    const current = state.next[0];
    if (count === 0 || !current) {
        return { ...state, nextOptions: null };
    }
    const options = [current];
    const same = (a, b) => a[0] === b[0] && a[1] === b[1];
    let pieceRng = state.pieceRng;
    for (let attempts = 0; options.length < count && attempts < count * 8; attempts++) {
        const generated = generatePieceColors(pieceRng, state.rules.colorCount);
        pieceRng = generated.state;
        const unique = !options.some((o) => same(o, generated.colors));
        if (unique || attempts >= count * 4) {
            options.push(generated.colors);
        }
    }
    events.push({ type: 'next-options', options });
    return { ...state, pieceRng, nextOptions: options };
}
/** Создаёт новую партию: поле с вирусами по seed, первая капсула и очередь следующих. */
export function createGame(options) {
    const rules = createRules(options.rules);
    const seed = normalizeSeed(options.seed);
    let board = applyMask(createBoard(rules.width, rules.height), rules.mask);
    if (rules.boss)
        board = placeBoss(board, rules.boss);
    board = placeViruses(board, rules, deriveSeed(seed, VIRUS_STREAM)).board;
    let boss = null;
    if (rules.boss) {
        const guarded = placeGuards(board, rules.boss.guards, deriveSeed(seed, BOSS_STREAM));
        board = guarded.board;
        boss = createBossState(rules, board, guarded.rng);
    }
    let pieceRng = deriveSeed(seed, PIECE_STREAM);
    const next = [];
    for (let i = 0; i < rules.previewSize; i++) {
        const generated = generatePieceColors(pieceRng, rules.colorCount);
        next.push(generated.colors);
        pieceRng = generated.state;
    }
    const initial = {
        rules,
        seed,
        board,
        active: null,
        next,
        pieceRng,
        status: 'playing',
        lossReason: null,
        score: 0,
        virusesRemaining: countViruses(board),
        piecesLocked: 0,
        lastChain: 0,
        bestChain: 0,
        nextOptions: null,
        boss,
        energy: rules.abilities.map(() => 0),
        effects: NO_EFFECTS,
        attackRng: deriveSeed(seed, ATTACK_STREAM),
    };
    return spawnPiece(initial, []);
}
/**
 * Цикл очисток: найти совпадения → удалить → очки и энергия → урон боссу → гравитация,
 * пока есть совпадения. blocked — клетки падающей капсулы, если она ещё в игре.
 */
function resolveClears(state, start, blocked, events) {
    let board = start;
    let score = state.score;
    let chain = 0;
    let gain = 0;
    let boss = state.boss;
    for (let matches = findMatches(board, state.rules.matchLength); matches.length > 0; matches = findMatches(board, state.rules.matchLength)) {
        chain++;
        const cleared = clearCells(board, matches);
        const points = scoreClear({ viruses: cleared.viruses, segments: cleared.segments, shields: cleared.shields, chain }, state.rules.scoring);
        score += points;
        gain += energyGain(cleared.viruses, cleared.shields, chain);
        events.push({ type: 'cells-cleared', chain, cells: cleared.cleared, points });
        let afterClear = cleared.board;
        if (boss && state.rules.boss) {
            const hit = hitBoss(afterClear, cleared.cleared, chain, boss, state.rules.boss, events);
            afterClear = hit.board;
            boss = hit.boss;
        }
        const settled = applyGravity(afterClear, blocked);
        board = settled.board;
        if (settled.moves > 0) {
            events.push({ type: 'gravity-settled', moves: settled.moves });
        }
    }
    return { board, boss, score, chain, gain };
}
function isWon(board, boss) {
    return boss ? boss.hp <= 0 : countViruses(board) === 0;
}
/**
 * Фиксация капсулы и разрешение последствий. Порядок:
 * 1) запись капсулы в поле; 2) цикл очисток (очки, энергия, урон боссу, гравитация);
 * 3) «Инфицирование» сопернику за цепочку от ×2 (Versus); 4) победа: босс повержен,
 * а без босса — вирусов нет; 5) ход босса (эффекты и способности); 6) поражение по лимиту
 * капсул; 7) появление новой капсулы или поражение, если стартовые клетки заняты.
 */
function lockPiece(state, piece, events) {
    const placedCells = pieceCells(piece);
    const cells = state.board.cells.slice();
    for (const c of placedCells) {
        cells[indexOf(state.board, c.x, c.y)] = { kind: 'segment', color: c.color, link: c.link };
    }
    events.push({ type: 'piece-locked', cells: placedCells });
    const outcome = resolveClears(state, { ...state.board, cells }, new Set(), events);
    let { board, boss } = outcome;
    const { chain } = outcome;
    if (state.rules.infection && chain >= ABILITY_TUNING.infectionMinChain) {
        events.push({
            type: 'attack-sent',
            attack: { kind: 'infection', count: Math.min(ABILITY_TUNING.infectionMax, chain) },
        });
    }
    const piecesLocked = state.piecesLocked + 1;
    const won = isWon(board, boss);
    let overflow = false;
    if (boss && !won) {
        const turn = bossTurn(board, boss, state.rules, events);
        board = turn.board;
        boss = turn.boss;
        overflow = turn.overflow;
    }
    const resolved = {
        ...state,
        board,
        boss,
        active: null,
        // Новые варианты появятся вместе со следующей капсулой; если партия окончена — их нет.
        nextOptions: null,
        score: outcome.score,
        virusesRemaining: countViruses(board),
        piecesLocked,
        lastChain: chain,
        bestChain: Math.max(state.bestChain, chain),
        energy: chargeEnergy(state.energy, state.rules.abilities, outcome.gain),
        effects: { ...state.effects, scan: Math.max(0, state.effects.scan - 1) },
    };
    if (won) {
        events.push({ type: 'game-won' });
        return { ...resolved, status: 'won' };
    }
    if (overflow) {
        events.push({ type: 'game-lost', reason: 'overflow' });
        return { ...resolved, status: 'lost', lossReason: 'overflow' };
    }
    if (state.rules.pieceLimit !== null && piecesLocked >= state.rules.pieceLimit) {
        events.push({ type: 'game-lost', reason: 'piece-limit' });
        return { ...resolved, status: 'lost', lossReason: 'piece-limit' };
    }
    return spawnPiece(resolved, events);
}
function rejected(state) {
    return { state, events: [], accepted: false };
}
function pieceIndices(state, piece) {
    return new Set(pieceCells(piece).map((c) => indexOf(state.board, c.x, c.y)));
}
/**
 * «Антибиотик»: бактерии исчезают (в «Биоплёнке» — теряют плёнку), затем гравитация
 * и обычный цикл очисток. Падающая капсула остаётся на месте и служит препятствием.
 */
function applyAntibiotic(state, piece, slot, events) {
    const targets = antibioticTargets(state.board, state.rules);
    if (targets.length === 0)
        return null;
    const cells = state.board.cells.slice();
    const affected = [];
    for (const i of targets) {
        const cell = cells[i];
        if (cell?.kind !== 'virus')
            continue;
        cells[i] = cell.shield
            ? cell.guard
                ? { kind: 'virus', color: cell.color, guard: true }
                : { kind: 'virus', color: cell.color }
            : EMPTY;
        affected.push({ x: i % state.board.width, y: Math.floor(i / state.board.width) });
    }
    events.push({ type: 'ability-used', slot, ability: 'antibiotic', cells: affected });
    const blocked = pieceIndices(state, piece);
    const settled = applyGravity({ ...state.board, cells }, blocked).board;
    const outcome = resolveClears(state, settled, blocked, events);
    const next = {
        ...state,
        board: outcome.board,
        boss: outcome.boss,
        score: outcome.score,
        virusesRemaining: countViruses(outcome.board),
        energy: chargeEnergy(state.energy, state.rules.abilities, outcome.gain),
    };
    if (isWon(outcome.board, outcome.boss)) {
        events.push({ type: 'game-won' });
        return { ...next, active: null, nextOptions: null, status: 'won' };
    }
    return next;
}
function useAbility(state, piece, slot) {
    const kind = Number.isInteger(slot) ? state.rules.abilities[slot] : undefined;
    if (kind === undefined || (state.energy[slot] ?? 0) < abilityCost(kind)) {
        return rejected(state);
    }
    const events = [];
    if (kind !== 'antibiotic')
        events.push({ type: 'ability-used', slot, ability: kind, cells: [] });
    const effects = state.effects;
    let next;
    switch (kind) {
        case 'cryo':
            next = { ...state, effects: { ...effects, frozen: ABILITY_TUNING.cryoTicks } };
            break;
        case 'scanner':
            next = { ...state, effects: { ...effects, scan: ABILITY_TUNING.scanCapsules } };
            break;
        case 'immunity':
            if (effects.immunity)
                return rejected(state);
            next = { ...state, effects: { ...effects, immunity: true } };
            break;
        case 'fever':
            next = state;
            events.push({
                type: 'attack-sent',
                attack: { kind: 'fever', ticks: ABILITY_TUNING.feverTicks },
            });
            break;
        case 'antibiotic': {
            const treated = applyAntibiotic(state, piece, slot, events);
            if (!treated)
                return rejected(state);
            next = treated;
            break;
        }
    }
    const energy = next.energy.map((e, i) => (i === slot ? 0 : e));
    return { state: { ...next, energy }, events, accepted: true };
}
/** Входящая атака соперника (Versus). Подаёт сервер, не игрок. */
function receiveAttack(state, piece, attack) {
    const events = [];
    if (state.effects.immunity) {
        events.push({ type: 'attack-blocked', attack });
        return {
            state: { ...state, effects: { ...state.effects, immunity: false } },
            events,
            accepted: true,
        };
    }
    if (attack.kind === 'fever') {
        events.push({ type: 'attack-received', attack, cells: [] });
        return {
            state: { ...state, effects: { ...state.effects, fever: Math.max(0, attack.ticks) } },
            events,
            accepted: true,
        };
    }
    const count = Math.max(0, Math.min(ABILITY_TUNING.infectionMax, Math.floor(attack.count)));
    const infected = dropInfection(state.board, state.rules, pieceIndices(state, piece), count, state.attackRng);
    events.push({ type: 'attack-received', attack, cells: infected.cells });
    return {
        state: {
            ...state,
            board: infected.board,
            attackRng: infected.rng,
            virusesRemaining: countViruses(infected.board),
        },
        events,
        accepted: true,
    };
}
/** Применяет одну команду. Чистая функция: исходное состояние не изменяется. */
export function step(state, command) {
    const piece = state.active;
    if (state.status !== 'playing' || piece === null) {
        return rejected(state);
    }
    const events = [];
    switch (command.type) {
        case 'choose-next': {
            const options = state.nextOptions;
            const colors = Number.isInteger(command.option) ? options?.[command.option] : undefined;
            if (!options || !colors) {
                return rejected(state);
            }
            events.push({ type: 'next-chosen', option: command.option, colors });
            return {
                state: { ...state, next: [colors, ...state.next.slice(1)], nextOptions: null },
                events,
                accepted: true,
            };
        }
        case 'use-ability':
            return useAbility(state, piece, command.slot);
        case 'receive-attack':
            return receiveAttack(state, piece, command.attack);
        case 'move-left':
        case 'move-right': {
            const moved = tryShift(state.board, piece, command.type === 'move-left' ? -1 : 1, 0);
            if (!moved) {
                return rejected(state);
            }
            events.push({ type: 'piece-moved', piece: moved });
            return { state: { ...state, active: moved }, events, accepted: true };
        }
        case 'rotate-clockwise':
        case 'rotate-counterclockwise': {
            const rotated = tryRotate(state.board, piece, command.type === 'rotate-clockwise' ? 1 : -1);
            if (!rotated) {
                return rejected(state);
            }
            events.push({ type: 'piece-rotated', piece: rotated });
            return { state: { ...state, active: rotated }, events, accepted: true };
        }
        case 'tick':
        case 'soft-drop': {
            let current = state;
            if (command.type === 'tick') {
                const { frozen, fever } = state.effects;
                current = {
                    ...state,
                    effects: {
                        ...state.effects,
                        frozen: Math.max(0, frozen - 1),
                        fever: Math.max(0, fever - 1),
                    },
                };
                // «Криотерапия»: тик гравитации проходит, но капсула не падает.
                if (frozen > 0)
                    return { state: current, events, accepted: true };
            }
            const fallen = tryShift(current.board, piece, 0, 1);
            if (fallen) {
                events.push({ type: 'piece-moved', piece: fallen });
                return { state: { ...current, active: fallen }, events, accepted: true };
            }
            return { state: lockPiece(current, piece, events), events, accepted: true };
        }
        case 'hard-drop': {
            const dropped = dropPosition(state.board, piece);
            if (dropped.y !== piece.y) {
                events.push({ type: 'piece-moved', piece: dropped });
            }
            return { state: lockPiece(state, dropped, events), events, accepted: true };
        }
    }
}
/**
 * Во сколько раз быстрее должны идти тики гравитации: «Цитокиновый шторм» босса и
 * «Лихорадка» соперника складываются. Темп задаёт клиент или сервер — движок времени не знает.
 */
export function gravityFactor(state) {
    return 1 + (state.boss && state.boss.storm > 0 ? 1 : 0) + (state.effects.fever > 0 ? 1 : 0);
}
/** Сколько следующих капсул видит игрок: одну, со «Сканером» — всю очередь. */
export function visibleNext(state) {
    return state.effects.scan > 0 ? state.next : state.next.slice(0, 1);
}
/** Проигрывает партию с нуля: одинаковые seed, правила и команды всегда дают одинаковое состояние. */
export function replay(options, commands) {
    let state = createGame(options);
    let accepted = 0;
    for (const command of commands) {
        const result = step(state, command);
        state = result.state;
        if (result.accepted) {
            accepted++;
        }
    }
    return { state, accepted, rejected: commands.length - accepted };
}
//# sourceMappingURL=game-engine.js.map